import * as Dicer from 'dicer'
import * as hat from 'hat'
import * as parser from 'http-string-parser'
import { List } from 'immutable'
import stringToStream = require('string-to-stream')
import { HttpResult } from 'widgets-for-react'

export type BatchRequest = {
  path: string
  method: 'GET'
  requestId?: string
  success?: (_: BatchRequestResponse) => Promise<void>
  error?: (_: BatchRequestResponse) => Promise<void>
}

export type BatchRequestGroup = {
  requests: BatchRequest[]
  success: (_: BatchRequestResponse[]) => Promise<void>
  error: (_: BatchRequestResponse[]) => Promise<void>
}

export type BatchRequestResponse = {
  data: HttpResult<string>
  requestId: string
}

const leftAllign = (str: string) =>
  str
    .trim()
    .split('\n')
    .map((x) => x.trim())
    .join('\n')

const batchContent = (request: BatchRequest) =>
  leftAllign(`
    Content-Type: application/json
    Content-Id: ${request.requestId}

    ${request.method} ${encodeURI(request.path)} HTTP/2.0
    Host: ${window.location.host}
  `)

const batchBodyFor = (requests: List<BatchRequestGroup>, boundary: string): string =>
  leftAllign(`
    --${boundary}
    ${requests
      .map((x) => x.requests)
      .flatMap((x) => List(x))
      .map((x) => batchContent(x))
      .join(`\n\n--${boundary}\n`)}
    --${boundary}--
  `)

const getBoundaryFromResponse = (response: Response) => {
  const boundaryRegex = /^multipart\/.+?(?:; boundary=(?:(?:"(.+)")|(?:([^\s]+))))$/i
  const matches = response.headers.get('content-type').match(boundaryRegex)
  if (!matches) {
    throw new Error('Unable to determine boundary')
  }
  return matches[1] || matches[2]
}

const executeRequestCallbacks = async (
  groups: List<BatchRequestGroup>,
  responses: BatchRequestResponse[]
): Promise<void> => {
  const promises = groups
    .map((x) => List(x.requests))
    .flatMap((x) => x)
    .map((request) => {
      const response = List(responses).find((x) => x.requestId == request.requestId)
      if (!response) {
        throw new Error('Unable to match response with single request')
      }

      if (response.data.kind != 'result' && !!request.error) {
        return request.error(response)
      }

      if (response.data.kind == 'result' && !!request.success) {
        return request.success(response)
      }
    })

  await Promise.all(promises.filter((x) => !!x).toArray())
}

const executeGroupCallbacks = async (
  groups: List<BatchRequestGroup>,
  responses: BatchRequestResponse[]
): Promise<void> => {
  const promises = groups.map((group) => {
    const requests = List(group.requests)
    const requestIds = requests.map((x) => x.requestId).toList()

    const groupResponses = List(responses).filter((x) => requestIds.filter((y) => y == x.requestId).count() > 0)

    if (groupResponses.count() !== requests.count()) {
      throw new Error('Unable to match response with request')
    }
    if (groupResponses.filter((x) => x.data.kind != 'result').count() == 0) {
      return group.success(groupResponses.toArray())
    } else {
      return group.error(groupResponses.toArray())
    }
  })

  await Promise.all(promises.toArray())
}

const sanatizeGroups = (groups: BatchRequestGroup[]): List<BatchRequestGroup> =>
  List(groups)
    .map((x) => ({
      ...x,
      requests: x.requests.map((r) => ({
        ...r,
        requestId: r.requestId ? r.requestId : hat.rack()()
      }))
    }))
    .toList()

const parseBatchResponse = async (response: Response): Promise<BatchRequestResponse[]> =>
  new Promise(async (resolve, _) => {
    const ret: BatchRequestResponse[] = []
    let dice = new Dicer({
      boundary: getBoundaryFromResponse(response)
    })

    dice
      .on('part', (part) => {
        let header: object
        let rawData: Array<string | Buffer> = []

        part
          .on('header', (h) => {
            header = h
          })
          .on('data', (data) => {
            rawData.push(data)
          })
          .on('end', () => {
            if (!rawData || rawData.length == 0) {
              return
            }

            const buffer = Buffer.concat(
              rawData.map((x) => Buffer.from(x.toString(), 'utf8')),
              rawData.map((x) => x.length).reduce((a, b) => a + b, 0)
            )
            const parsed = parser.parseResponse(buffer.toString())
            const statusCode = parseInt(parsed.statusCode)

            ret.push({
              data:
                statusCode >= 200 && statusCode <= 299
                  ? {
                      kind: 'result',
                      value: parsed.body,
                      status: statusCode
                    }
                  : {
                      kind: 'failed',
                      status: statusCode
                    },
              requestId: header['content-id']
            })
          })
      })
      .on('finish', () => {
        resolve(ret)
      })

    const stream = stringToStream(await response.text())
    stream.pipe(dice)
  })

const hasNoRequests = (groups: List<BatchRequestGroup>): boolean =>
  groups.count() == 0 ||
  List(groups)
    .map((x) => List(x.requests))
    .flatMap((x) => x)
    .count() == 0

const performSingleRequest = async (group: BatchRequestGroup): Promise<BatchRequestResponse[]> => {
  if (group.requests.length != 1) {
    throw new Error('Can only perform single request when group only has 1 request')
  }
  const request = group.requests[0]

  const ret = await fetch(request.path, {
    method: request.method,
    credentials: 'include'
  })

  return [
    {
      data: ret.ok
        ? {
            kind: 'result',
            status: ret.status,
            value: await ret.text()
          }
        : { kind: 'failed', status: ret.status },
      requestId: request.requestId
    }
  ]
}

const fetchGroupResponses = async (groups: List<BatchRequestGroup>): Promise<BatchRequestResponse[]> => {
  // We can do a normal fetch when there's only a single request
  if (groups.count() == 1 && groups.first().requests.length == 1) {
    return performSingleRequest(groups.first())
  }

  const boundary = `batch_${hat.rack()()}`

  const ret = await fetch('/batch', {
    method: 'POST',
    credentials: 'include',
    headers: new Headers({
      'content-type': `multipart/batch; boundary="${boundary}"`,
      'X-XSRF-TOKEN': (document.getElementsByName('__RequestVerificationToken')[0] as HTMLInputElement).value
    }),
    body: batchBodyFor(groups, boundary)
  })

  if (!ret.ok) {
    throw new Error('Error in fetching batch request')
  }

  return await parseBatchResponse(ret)
}

export const performBatchRequest = async (rawGroups: BatchRequestGroup[]): Promise<BatchRequestResponse[]> => {
  const groups = sanatizeGroups(rawGroups)

  if (hasNoRequests(groups)) {
    return []
  }

  const response = await fetchGroupResponses(groups)
  await executeRequestCallbacks(groups, response)
  await executeGroupCallbacks(groups, response)
  return response
}
